import { useMemo, useState } from "react";
import { Nav, NavItem, NavLink, Button, Badge } from "reactstrap";
import { FormattedMessage } from "react-intl";
import ResultTable from "../Results/ResultTable";
import { buildExamTableData } from "../../Utilities/buildExamSubset";

const trimmedExamColumns = ["donePercentage", "activityPoints"];

const examCourseName = (row) => (
  <>
    <FormattedMessage id="FinalExamFor" /> {row.name}
    {row.examTotal ? (
      <span className="text-muted fw-normal small ms-1">
        (
        <FormattedMessage id="NumberOfExams" values={{ count: row.examTotal }} />
        )
      </span>
    ) : (
      ""
    )}
  </>
);

const examStatusBadge = (row) => {
  const s = [
    { color: "success", count: row.examPassed, id: "ExamsCountPassed" },
    { color: "danger", count: row.examFailed, id: "ExamsCountFailed" },
    { color: "warning", count: row.examInProgress, id: "ExamsCountInProgress" },
    {
      color: "secondary",
      count: row.examNotStarted,
      id: "ExamsCountNotStarted",
    },
  ].find((x) => x.count > 0);

  if (!s) return null;

  return (
    <Badge color={s.color} pill className="badge-pill">
      <span className="fw-normal px-2">
        <FormattedMessage id={s.id} values={{ count: s.count }} />
      </span>
    </Badge>
  );
};

export default function StudentResultTables({
  tableData = [],
  columns = [],
  columnExtensions = [],
}) {
  const [activeTab, setActiveTab] = useState("all");
  const [examCmd, setExamCmd] = useState({ expand: false });
  const [allCmd, setAllCmd] = useState({ expand: false });

  const examTableData = useMemo(
    () =>
      buildExamTableData(tableData).map((row) =>
        row.parentId == null
          ? {
              ...row,
              isCourseHeader: true,
              name: examCourseName(row),
              ...(row.examTotal ? { status: examStatusBadge(row) } : {}),
            }
          : row
      ),
    [tableData]
  );

  const examColumns = useMemo(
    () => columns.filter((c) => !trimmedExamColumns.includes(c.name)),
    [columns]
  );
  const examColumnExtensions = useMemo(
    () =>
      columnExtensions
        .filter((c) => !trimmedExamColumns.includes(c.columnName))
        .map((c) => (c.columnName === "name" ? { ...c, width: 360 } : c)),
    [columnExtensions]
  );

  const activeCmd = activeTab === "all" ? allCmd : examCmd;
  const setActiveCmd = activeTab === "all" ? setAllCmd : setExamCmd;
  const showExpandToggle = activeTab === "all" || examTableData.length > 0;

  return (
    <>
      <div className="result-tabs-bar d-flex justify-content-between align-items-end mb-3">
        <Nav tabs className="result-tabs">
          <NavItem>
            <NavLink
              active={activeTab === "all"}
              onClick={() => setActiveTab("all")}
            >
              <FormattedMessage id="AllResults" />
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={activeTab === "exams"}
              onClick={() => setActiveTab("exams")}
            >
              <FormattedMessage id="FinalExams" />
            </NavLink>
          </NavItem>
        </Nav>
        {showExpandToggle && (
          <Button
            color="primary"
            outline
            className="btn-sm result-tabs-action"
            onClick={() => setActiveCmd({ expand: !activeCmd.expand })}
          >
            <FormattedMessage
              id={activeCmd.expand ? "CollapseAll" : "ExpandAll"}
            />
          </Button>
        )}
      </div>
      {activeTab === "all" ? (
        <ResultTable
          key="all-results"
          level="Student"
          tableData={tableData}
          columnExtensions={columnExtensions}
          columns={columns}
          rightAlignedColumns={[
            "donePercentage",
            "score",
            "nbrOfAttempts",
            "focusLostCount",
            "activityPoints",
          ]}
          centerAlignedColumns={["status"]}
          defaultExpanded={allCmd.expand ? true : {}}
          expandCommand={allCmd}
        />
      ) : examTableData.length === 0 ? (
        <p className="text-muted text-center my-4">
          <FormattedMessage id="NoFinalExams" />
        </p>
      ) : (
        <ResultTable
          key="final-exams"
          level="Student"
          tableData={examTableData}
          columnExtensions={examColumnExtensions}
          columns={examColumns}
          rightAlignedColumns={["score", "nbrOfAttempts", "focusLostCount"]}
          centerAlignedColumns={["status"]}
          defaultExpanded={examCmd.expand ? true : {}}
          expandCommand={examCmd}
          hideInfoIcons
          nonSortableColumns={["status"]}
        />
      )}
    </>
  );
}
